import { useState, useEffect, useMemo, useCallback } from 'react'
import Table from '../../common/Table'
import LegislationDocumentModal from '../modals/LegislationDocumentModal'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  GalleryVerticalEnd,
  PencilIcon,
} from 'lucide-react'
import DateTimeCell from '../../common/DateTimeCell'
import { useFetch } from '../../../hooks/useCallApi'

function LegislativeDocumentTable({
  search = '',
  createdByFilter = '',
  versionFilter = '',
  header,
  activeTab,
}) {
  const [expandedItems, setExpandedItems] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [modalMode, setModalMode] = useState('create')
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, currentPage: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const params = useMemo(
    () => ({
      per_page: pagination.perPage,
      page: pagination.currentPage,
      search: search || undefined,
      created_by: createdByFilter || undefined,
      version_number: versionFilter || undefined,
      section: 'legislative_document',
    }),
    [pagination, search, createdByFilter, versionFilter]
  )
  const [versionId, setVersionId] = useState(null)
  const {
    data: legislativeData,
    isLoading,
    refetch,
  } = useFetch(['legislative-documents', params], (api) =>
    api.admin.legislation.getTabView(params)
  )
  const { data: versionData } = useFetch(
    ['get-version', versionId],
    (api) => api.admin.legislation.getVersionId(versionId),
    { enabled: !!versionId }
  )

  useEffect(() => {
    if (versionData?.data?.legislative_file?.url && versionId) {
      window.open(versionData.data.legislative_file.url, '_blank')
      setVersionId(null)
    }
  }, [versionData, versionId])
  const tableRows = Array.isArray(legislativeData?.data?.data)
    ? legislativeData?.data?.data
    : []

  useEffect(() => {
    if (activeTab === 'Legislative Document') refetch()
  }, [activeTab, refetch])

  const toggleItem = useCallback((id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleEdit = (doc, version) => {
    setSelectedDocument(doc)
    setSelectedVersion(version)
    setModalMode('edit')
    setIsModalOpen(true)
  }

  const handleUploadNewVersion = (doc) => {
    setSelectedDocument(doc)
    setSelectedVersion(null)
    setModalMode('create')
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedDocument(null)
    setSelectedVersion(null)
  }

  const handlePageChange = (page, perPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }))
  }

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const versionColumns = [
    {
      key: 'version_number',
      label: 'Version Number',
      render: (_, version) => (
        <button
          className='text-blue-600 text-sm underline hover:text-blue-800 transition-colors'
          onClick={() => {
            setVersionId(null)
            setTimeout(() => setVersionId(version.version_id), 0)
          }}
        >
          {console.log('ver', version)}
          Version {version.version_number}
        </button>
      ),
    },
    {
      key: 'revision_summary',
      label: 'Revision Summary',
      render: (_, version) => (
        <div
          className='block w-full text-sm max-w-xs line-clamp-2 break-words whitespace-normal'
          title={version.revision_summary}
        >
          {version.revision_summary}
        </div>
      ),
    },
    {
      key: 'created_by',
      label: 'Created By',
      render: (_, version) => (
        <span className='text-gray-500 text-sm'>{version.created_by}</span>
      ),
    },
    {
      key: 'is_final',
      label: 'Final Version',
      render: (_, version) =>
        version.is_final ? (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            className='w-5 h-5 text-green-600'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
            />
          </svg>
        ) : (
          <span className='text-gray-300'>-</span>
        ),
    },
    {
      key: 'created_at',
      label: 'Created At',
      render: (_, version) => <DateTimeCell value={version.created_at} />,
    },
    {
      key: 'updated_at',
      label: 'Updated At',
      render: (_, version) => <DateTimeCell value={version.updated_at} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, version, doc) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleEdit(doc, version)
          }}
          className='text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110'
          title='Edit'
        >
          <PencilIcon className='h-4 w-4' />
        </button>
      ),
    },
  ]

  const columns = [
    {
      key: 'name',
      render: (_, doc) => (
        <div>
          <div
            className='flex items-center cursor-pointer hover:bg-gray-50 transition-colors rounded'
            onClick={() => toggleItem(doc.legislative_document_id)}
          >
            <div className='w-4 h-4 flex items-center justify-center mr-3 text-gray-500'>
              {expandedItems[doc.legislative_document_id] ? (
                <ChevronDownIcon className='w-4 h-4' />
              ) : (
                <ChevronRightIcon className='w-4 h-4' />
              )}
            </div>
            <div className='flex text-sm items-center gap-10'>
              <h3 className='font-medium text-gray-900 text-sm'>
                {capitalizeFirst(doc.legislation_type)} No.{' '}
                {doc.document_number}
              </h3>
              <div className='flex items-center text-gray-500 text-sm gap-2'>
                <GalleryVerticalEnd className='w-4 h-4' />
                {doc.versions?.length || 0}
              </div>
            </div>
          </div>

          {expandedItems[doc.legislative_document_id] && (
            <div className='my-4 rounded-lg'>
              <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
                <Table
                  noContainer
                  columns={versionColumns.map((col) => ({
                    ...col,
                    render: (value, version) =>
                      col.key === 'actions'
                        ? col.render(value, version, doc)
                        : col.render(value, version),
                  }))}
                  data={doc.versions || []}
                  getRowKey={(version) => version.id}
                />
              </div>

              <div className='mt-4 flex justify-center'>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleUploadNewVersion(doc)
                  }}
                  className='flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='w-4 h-4'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M12 4.5v15m7.5-7.5h-15'
                    />
                  </svg>
                  Upload a New Version
                </button>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <Table
        header={header}
        hideHeader
        columns={columns}
        data={tableRows}
        getRowKey={(row) => row.id}
        pagination={{
          currentPage: pagination.currentPage,
          perPage: pagination.perPage,
          lastPage: legislativeData?.data?.meta?.last_page || 1,
          total: legislativeData?.data?.meta?.total || 0,
        }}
        onPageChange={handlePageChange}
        onPerPageChange={(value) =>
          setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }))
        }
        isLoading={isLoading}
      />

      <LegislationDocumentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        document={selectedDocument}
        version={selectedVersion}
        onSuccess={() => refetch()}
        mode={modalMode}
        capitalizeFirst={capitalizeFirst}
      />
    </div>
  )
}

export default LegislativeDocumentTable
